import type { SkyPalette } from '../../shared/conditions'

/** sRGB hex / rgba() → [r, g, b, a] with r,g,b in 0–255 and a in 0–1. */
export function parseColor(c: string): [number, number, number, number] {
  if (c.startsWith('#')) {
    const v = parseInt(c.slice(1, 7), 16)
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255, 1]
  }
  const parts = c.match(/[\d.]+/g)?.map(Number) ?? [0, 0, 0, 1]
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0, parts[3] ?? 1]
}

const lin = (v: number) => {
  const s = v / 255
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}

export function luminance([r, g, b]: [number, number, number] | number[]): number {
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

export function contrast(a: number[], b: number[]): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** Source-over composite in sRGB space, the way browsers blend translucent fills. */
export function over(top: number[], alpha: number, under: number[]): number[] {
  return [0, 1, 2].map((i) => top[i] * alpha + under[i] * (1 - alpha))
}

const WHITE = [255, 255, 255]

/** Secondary text on glass (--text-2): 74 % white. */
export const TEXT_2_ALPHA = 0.74

/** The sky behind a surface, worst case: its lightest stop lifted by bright drifting clouds. */
function lightBackdrop(stops: string[], cloudLight: number): number[] {
  const rgb = stops.map((s) => parseColor(s).slice(0, 3))
  const lightest = rgb.reduce((best, s) => (luminance(s) > luminance(best) ? s : best))
  return over([248, 250, 255], Math.max(0, Math.min(1, cloudLight)), lightest)
}

/** Contrast of white at `textAlpha` composited over `bg`. */
function textContrast(textAlpha: number, bg: number[]): number {
  return contrast(over(WHITE, textAlpha, bg), bg)
}

/**
 * How the rendered sky differs from its palette stops, for the contrast solves below:
 * - cloudLight: bright cloud drifting behind a surface (0–1, lifts toward white);
 * - glow: 0–1 extra light the renderer adds that the stops don't carry — fog banks and a
 *   low sun's horizon glow (App passes max(fog, twilight)). Measured in the browser, these
 *   scenes render up to ~1.9× their stops' luminance where text sits; clear and cloudy
 *   day skies about 1.6×.
 */
export interface SkyLight {
  cloudLight?: number
  glow?: number
}

/**
 * The glass for cards on a given sky — the brief's contrast floor ("use the card tint, not
 * the raw sky"), checked against the lightest stop of that sky plus bright clouds:
 * - fill: the palette's own card tint, opaque enough that white text clears 4.6:1;
 * - dim: the backdrop filter's brightness (0.9 by default), lowered on light skies (fog,
 *   snow, bright overcast, a pale horizon) until secondary text (74 % white — card headers,
 *   notes) also clears 4.5:1 with a margin for the glass's lit corner and the brighter
 *   render. Dimming the blurred sky keeps the glass translucent instead of painting it
 *   opaque; only if that isn't enough does the tint thicken further.
 */
export function glassFill(pal: SkyPalette, light: number | SkyLight = 0): { fill: string; line: string; alpha: number; dim: number } {
  const { cloudLight = 0, glow = 0 } = typeof light === 'number' ? { cloudLight: light } : light
  const [r, g, b, a0] = parseColor(pal.card)
  const backdrop = lightBackdrop([pal.top, pal.mid, pal.bottom], cloudLight)
  const bgAt = (alpha: number, dim: number, back = backdrop) => over([r, g, b], alpha, back.map((v) => v * dim))
  let alpha = Math.max(a0, 0.2)
  while (alpha < 0.8 && textContrast(1, bgAt(alpha, 0.9)) < 4.6) alpha += 0.02
  const lifted = over([248, 250, 255], 0.12, backdrop)
  const target = 5 + 0.5 * glow
  let dim = 0.9
  while (dim > 0.5 && textContrast(TEXT_2_ALPHA, bgAt(alpha, dim, lifted)) < target) dim -= 0.02
  while (alpha < 0.9 && textContrast(TEXT_2_ALPHA, bgAt(alpha, dim, lifted)) < target) alpha += 0.02
  return { fill: `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`, line: pal.line, alpha, dim: Number(dim.toFixed(2)) }
}

/**
 * The lighter, more translucent glass the home screen's cards are drawn with — the sky
 * shows through. `glassFill` above is the heavy sheet/sidebar tint; the tiers here keep the
 * card's own tint thin and let the backdrop's brightness carry the contrast instead, so the
 * blurred sky stays visible (a smoked pane, not a painted slab):
 * - card: hourly, 10-day, map, Time Machine — the primary content surface;
 * - tile: the small metric tiles — a touch lighter again.
 * Both clear the same bar as `glassFill`: 74 % white (secondary text) ≥ 5:1 over the lifted
 * worst-case backdrop, so every line of body text is ≥ 4.5:1 with the margin for the lit
 * corner and the brighter render.
 */
export type GlassTier = 'card' | 'tile'

export const TIER_ALPHA: Record<GlassTier, number> = { card: 0.3, tile: 0.22 }

/**
 * White the card's own highlight draws over its tint (the soft light at the top edge, styles.css
 * `.card-bg`) plus the extra veil a hovered, openable card takes — in the solve, so the contrast
 * guarantee holds for the lit corner and the hover state too.
 */
/** Contrast the tiers solve to for 74 % white (secondary text); the floor is 4.5 — the rest is margin. */
export const TIER_TARGET = 5.1

export const TIER_VEIL: Record<GlassTier, number> = { card: 0.06, tile: 0.05 }

/** Lowest backdrop brightness the solve may reach before it thickens the tint instead. */
const MIN_TIER_DIM = 0.24

export function glassTier(pal: SkyPalette, tier: GlassTier, light: number | SkyLight = 0): { fill: string; alpha: number; dim: number } {
  const { cloudLight = 0, glow = 0 } = typeof light === 'number' ? { cloudLight: light } : light
  const [r, g, b, a0] = parseColor(pal.card)
  const backdrop = over([248, 250, 255], 0.12, lightBackdrop([pal.top, pal.mid, pal.bottom], cloudLight))
  const bgAt = (alpha: number, dim: number) => over(WHITE, TIER_VEIL[tier], over([r, g, b], alpha, backdrop.map((v) => v * dim)))
  const target = TIER_TARGET + 0.5 * glow
  // Dark skies carry their own depth: a palette's own thin tint is enough (and never lighter than the tier).
  let alpha = Math.min(TIER_ALPHA[tier], Math.max(a0, 0.16))
  let dim = 0.96
  while (dim > MIN_TIER_DIM && textContrast(TEXT_2_ALPHA, bgAt(alpha, dim)) < target) dim -= 0.02
  while (alpha < 0.9 && textContrast(TEXT_2_ALPHA, bgAt(alpha, dim)) < target) alpha += 0.02
  return { fill: `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`, alpha, dim: Number(dim.toFixed(2)) }
}

/** The top-right control capsule's white veil at rest, and under a hovered icon. */
export const CONTROL_VEIL = 0.15
export const CONTROL_VEIL_HOVER = 0.22

/**
 * Backdrop brightness behind the home screen's floating controls (the map / Studio /
 * settings capsule): over a pale sky a white-veiled capsule left its white icons at ~1.5:1,
 * so the blurred sky behind it is dimmed until the icons clear 4.5:1 at rest (and 3.5:1 under
 * a hovered icon's extra veil) — 1.06, a touch of lift, on skies that are dark enough already.
 */
export function chromeDim(pal: SkyPalette, light: SkyLight = {}): number {
  const { cloudLight = 0, glow = 0 } = light
  const backdrop = over([248, 250, 255], 0.12, lightBackdrop([pal.top, pal.mid], cloudLight))
  const target = 4.6 + 0.4 * glow
  let dim = 1.06
  while (dim > 0.3 && textContrast(1, over(WHITE, CONTROL_VEIL, backdrop.map((v) => Math.min(255, v * dim)))) < target) dim -= 0.02
  return Number(dim.toFixed(2))
}

/** 0 for a saturated blue sky … 1 for a grey or pale one (fog, snow, overcast, smoke, dusk). */
export function skyGreyness(pal: SkyPalette): number {
  const chroma = Math.min(
    ...[pal.top, pal.mid].map((c) => {
      const [r, g, b] = parseColor(c)
      return (Math.max(r, g, b) - Math.min(r, g, b)) / 255
    }),
  )
  return Math.max(0, Math.min(1, (0.45 - chroma) / 0.25))
}

/**
 * Opacity of the navy scrim behind the hero (city, temperature, condition, H/L) over the
 * upper sky as rendered — 0 on skies dark enough already (night, storms). On grey and pale
 * skies (fog, snow, bright overcast) every hero line clears 4.6:1; a saturated blue sky
 * already carries white type, so there the solve holds large-text contrast (3:1+) and
 * keeps Apple's bright blue instead of dimming it.
 */
export function heroScrim(pal: SkyPalette, light: SkyLight = {}): number {
  const { cloudLight = 0, glow = 0 } = light
  const sky = Math.min(1, luminance(lightBackdrop([pal.top, pal.mid], cloudLight * 0.8)) * (1.6 + 0.3 * glow))
  const navy = luminance([10, 20, 40])
  const target = 3.3 + (4.6 + 0.3 * glow - 3.3) * skyGreyness(pal)
  let s = 0
  while (s < 0.76 && 1.05 / (sky * (1 - s) + navy * s + 0.05) < target) s += 0.02
  return Number(s.toFixed(2))
}

/**
 * Backdrop brightness for the locations sidebar so its own text (the 92 % white title,
 * the search field) clears 4.5:1: its fill is a thin mix of the card tint, so on light
 * skies the blurred sky behind it is dimmed instead (1.04 — a touch of lift — by default).
 */
export function sidebarDim(pal: SkyPalette, glass: { fill: string }, light: SkyLight = {}): number {
  const { cloudLight = 0, glow = 0 } = light
  const [r, g, b, a] = parseColor(glass.fill)
  const backdrop = over([248, 250, 255], 0.12, lightBackdrop([pal.top, pal.mid, pal.bottom], cloudLight))
  // color-mix(--card 38 %, rgba(10,16,32,.2)) in the sidebar rule, as one translucent layer.
  const fa = 0.38 * a + 0.62 * 0.2
  const fill = [0, 1, 2].map((i) => (0.38 * a * [r, g, b][i] + 0.62 * 0.2 * [10, 16, 32][i]) / fa)
  const target = 5 + 0.5 * glow
  let dim = 1.04
  while (dim > 0.5 && textContrast(0.92, over(fill, fa, backdrop.map((v) => Math.min(255, v * dim)))) < target) dim -= 0.02
  return Number(dim.toFixed(2))
}

/** Black scrim opacity that keeps white text ≥ 4.5:1 on a raw sky gradient (list cards). */
export function scrimFor(pal: SkyPalette): number {
  const stops = [pal.top, pal.mid, pal.bottom].map((s) => parseColor(s).slice(0, 3))
  let alpha = 0
  const ok = () => stops.every((s) => contrast(WHITE, over([0, 0, 0], alpha, s)) >= 4.5)
  while (alpha < 0.6 && !ok()) alpha += 0.02
  return Number(alpha.toFixed(2))
}

// ─── Temperature ramp (continuous cold → hot) ─────────────────────────────────

/** Stops in °C. Tuned to read like Apple's range bars: violet-blue cold, green mild, orange-red hot. */
const TEMP_STOPS: [number, [number, number, number]][] = [
  [-25, [148, 120, 255]],
  [-12, [104, 140, 255]],
  [0, [84, 190, 250]],
  [8, [92, 214, 206]],
  [15, [150, 220, 120]],
  [21, [240, 214, 72]],
  [27, [255, 164, 58]],
  [33, [255, 104, 60]],
  [40, [230, 50, 70]],
]

export function tempColor(c: number): string {
  const s = TEMP_STOPS
  if (c <= s[0][0]) return rgb(s[0][1])
  if (c >= s[s.length - 1][0]) return rgb(s[s.length - 1][1])
  for (let i = 1; i < s.length; i++) {
    if (c <= s[i][0]) {
      const [t0, c0] = s[i - 1]
      const [t1, c1] = s[i]
      const t = (c - t0) / (t1 - t0)
      return rgb([0, 1, 2].map((k) => c0[k] + (c1[k] - c0[k]) * t))
    }
  }
  return rgb(s[s.length - 1][1])
}

/** A CSS gradient spanning lowC → highC with intermediate stops so the ramp stays continuous. */
export function tempGradient(lowC: number, highC: number, angle = 90): string {
  if (!(highC > lowC)) return tempColor(lowC)
  const n = 5
  const stops = Array.from({ length: n }, (_, i) => {
    const t = lowC + ((highC - lowC) * i) / (n - 1)
    return `${tempColor(t)} ${Math.round((i / (n - 1)) * 100)}%`
  })
  return `linear-gradient(${angle}deg, ${stops.join(', ')})`
}

function rgb(c: number[]): string {
  return `rgb(${c.map((v) => Math.round(v)).join(', ')})`
}

// ─── Index scales ─────────────────────────────────────────────────────────────

export const AQI_GRADIENT =
  'linear-gradient(90deg, #3ecf6e 0%, #f5d63d 20%, #ff9a3c 35%, #f0524f 50%, #a45bd8 70%, #8a2442 100%)'

export function aqiColor(aqi: number): string {
  if (aqi <= 50) return '#3ecf6e'
  if (aqi <= 100) return '#f5d63d'
  if (aqi <= 150) return '#ff9a3c'
  if (aqi <= 200) return '#f0524f'
  if (aqi <= 300) return '#a45bd8'
  return '#8a2442'
}

/** Position 0–1 along the AQI bar (0–300+, with the 0–200 range given more room like Apple). */
export function aqiPosition(aqi: number): number {
  const pts: [number, number][] = [
    [0, 0],
    [50, 0.2],
    [100, 0.35],
    [150, 0.5],
    [200, 0.7],
    [300, 0.9],
    [500, 1],
  ]
  return piecewise(aqi, pts)
}

export const UV_GRADIENT = 'linear-gradient(90deg, #3ecf6e 0%, #f5d63d 25%, #ff9a3c 50%, #f0524f 72%, #a45bd8 100%)'

export function uvCategory(uv: number): string {
  if (uv < 3) return 'Low'
  if (uv < 6) return 'Moderate'
  if (uv < 8) return 'High'
  if (uv < 11) return 'Very High'
  return 'Extreme'
}

export function uvColor(uv: number): string {
  if (uv < 3) return '#3ecf6e'
  if (uv < 6) return '#f5d63d'
  if (uv < 8) return '#ff9a3c'
  if (uv < 11) return '#f0524f'
  return '#a45bd8'
}

export function piecewise(v: number, pts: [number, number][]): number {
  if (v <= pts[0][0]) return pts[0][1]
  for (let i = 1; i < pts.length; i++) {
    if (v <= pts[i][0]) {
      const [x0, y0] = pts[i - 1]
      const [x1, y1] = pts[i]
      return y0 + ((v - x0) / (x1 - x0)) * (y1 - y0)
    }
  }
  return pts[pts.length - 1][1]
}

/** Family colors for model members (stable, distinguishable on dark glass). */
export const FAMILY_COLORS: Record<string, string> = {
  nbm: '#7fd3ff',
  ncep: '#ffb86b',
  ecmwf: '#9d8cff',
  dwd: '#ff7f9e',
  cmc: '#ff6b6b',
  ukmo: '#5fe0b0',
  mf: '#f5e26b',
  jma: '#c0c8d8',
  metno: '#a3e36b',
}

export function familyColor(family: string): string {
  return FAMILY_COLORS[family] ?? '#c9d3e6'
}
